/**
 * WarningBanner Component
 *
 * Visar aktiva vädervarningar som påverkar rutten.
 * Expanderbar lista med färgkodade varningsnivåer.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';
import type { WeatherWarning, WarningLevel, WarningEvent } from '@/types';

interface WarningBannerProps {
  warnings: WeatherWarning[];
  onDismiss?: () => void;
}

/**
 * Få bakgrundsfärg för varningsnivå
 */
function getWarningStyles(level: WarningLevel): string {
  switch (level) {
    case 'RED':
      return 'bg-red-50 border-red-500 text-red-800';
    case 'ORANGE':
      return 'bg-orange-50 border-orange-500 text-orange-800';
    case 'YELLOW':
      return 'bg-yellow-50 border-yellow-500 text-yellow-800';
    case 'MESSAGE':
    default:
      return 'bg-blue-50 border-blue-500 text-blue-800';
  }
}

/**
 * Få ikon för varningstyp
 */
function getWarningIcon(event: WarningEvent): string {
  const icons: Record<WarningEvent, string> = {
    RAIN: '🌧️',
    SNOW: '❄️',
    WIND: '💨',
    STORM: '🌪️',
    THUNDERSTORM: '⛈️',
    ICE: '🧊',
    FIRE_RISK: '🔥',
    HIGH_TEMPERATURE: '🌡️',
    LOW_TEMPERATURE: '🥶',
    FLOODING: '🌊',
    COASTAL_FLOODING: '🌊',
    OTHER: '⚠️',
  };
  return icons[event] || '⚠️';
}

/**
 * Formatera datum för visning
 */
function formatDateRange(from: Date, to: Date): string {
  const fromStr = from.toLocaleDateString('sv-SE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const toStr = to.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${fromStr} - ${toStr}`;
}

/**
 * Sortera varningar efter allvarlighetsgrad
 */
function sortWarningsByLevel(warnings: WeatherWarning[]): WeatherWarning[] {
  const levelOrder: Record<WarningLevel, number> = {
    RED: 0,
    ORANGE: 1,
    YELLOW: 2,
    MESSAGE: 3,
  };

  return [...warnings].sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);
}

export function WarningBanner({ warnings, onDismiss }: WarningBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (warnings.length === 0) {
    return null;
  }

  const sortedWarnings = sortWarningsByLevel(warnings);
  const highestLevel = sortedWarnings[0]?.level || 'MESSAGE';
  const bannerStyles = getWarningStyles(highestLevel);

  return (
    <div className={`border-l-4 rounded-lg ${bannerStyles} animate-slide-up`}>
      {/* Huvud-banner */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="text-left">
            <span className="font-semibold">
              {warnings.length === 1
                ? '1 vädervarning påverkar din rutt'
                : `${warnings.length} vädervarningar påverkar din rutt`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
          {onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Stäng"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </button>

      {/* Expanderad lista */}
      {isExpanded && (
        <div className="border-t border-current/10 px-4 py-3 space-y-3">
          {sortedWarnings.map((warning) => (
            <WarningItem key={warning.id} warning={warning} />
          ))}
        </div>
      )}
    </div>
  );
}

interface WarningItemProps {
  warning: WeatherWarning;
}

function WarningItem({ warning }: WarningItemProps) {
  const levelStyles = getWarningStyles(warning.level);
  const icon = getWarningIcon(warning.event);

  return (
    <div className={`p-3 rounded-lg bg-white/50 ${levelStyles} border`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium">
            {warning.description.sv}
          </div>
          <div className="text-sm mt-1 opacity-80">
            📍 {warning.area.name}
          </div>
          <div className="text-xs mt-1 opacity-70">
            🕐 {formatDateRange(warning.validFrom, warning.validTo)}
          </div>
        </div>
        <LevelBadge level={warning.level} />
      </div>
    </div>
  );
}

interface LevelBadgeProps {
  level: WarningLevel;
}

function LevelBadge({ level }: LevelBadgeProps) {
  const labels: Record<WarningLevel, string> = {
    RED: 'Klass 3',
    ORANGE: 'Klass 2',
    YELLOW: 'Klass 1',
    MESSAGE: 'Info',
  };

  const colors: Record<WarningLevel, string> = {
    RED: 'bg-red-500 text-white',
    ORANGE: 'bg-orange-500 text-white',
    YELLOW: 'bg-yellow-400 text-yellow-900',
    MESSAGE: 'bg-blue-500 text-white',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-bold ${colors[level]}`}>
      {labels[level]}
    </span>
  );
}

/**
 * Compact variant för att visa i header eller sidebar
 */
export function WarningBadge({ warnings }: { warnings: WeatherWarning[] }) {
  if (warnings.length === 0) {
    return (
      <span className="text-green-500 font-medium">Inga varningar</span>
    );
  }

  const highestLevel = sortWarningsByLevel(warnings)[0]?.level || 'MESSAGE';

  const colors: Record<WarningLevel, string> = {
    RED: 'bg-red-500',
    ORANGE: 'bg-orange-500',
    YELLOW: 'bg-yellow-400',
    MESSAGE: 'bg-blue-500',
  };

  return (
    <span className={`
      ${colors[highestLevel]}
      text-white font-bold px-2 py-1 rounded-full text-sm
      flex items-center gap-1
    `}>
      <AlertTriangle className="w-3.5 h-3.5" />
      {warnings.length}
    </span>
  );
}
